import { useTranslation } from 'react-i18next';
import { ClbTooltip, ClbButton } from '@celebration/react';
import { useCallback, useState } from 'react';
import { ILoadFilterSimple } from './ILoadFilterSimple';
import SelectAutocomplete from '../../select-autocomplete';
import Input from '../../input';

export default function LoadFilterSimple(props: Readonly<ILoadFilterSimple>) {
    const {
        filters,
        setOpenAdvancedFilter,
        setFilters,
        getLoadChannelOptions,
        getLoadStatusOptions,
        getLoadCategoryOptions,
        hideLoadField,
    } = props;
    const { t } = useTranslation();

    const [load, setLoad] = useState<string>('');

    const concatUniqueIds = (loadsIds: string[], newLoads: string[]) => {
        return Array.from(new Set(loadsIds.concat(newLoads)));
    };

    const sanitizeFilterIds = (data: string) => {
        const ids = data.split(/[,;\s]/);
        return Array.from(
            new Set(
                ids
                    .map(element =>
                        element.replace(/[^a-zA-Z0-9]/g, '').toUpperCase(),
                    )
                    .filter(sanitizedValue => sanitizedValue !== ''),
            ),
        );
    };

    const onKeyDown = useCallback(
        (e: any) => {
            if (e.key === 'Enter') {
                const newLoads: string[] = sanitizeFilterIds(e.target?.value);
                const loadsIds: string[] = filters.current?.loadId ?? [];

                if (newLoads?.length > 0) {
                    setFilters({
                        ...filters.current,
                        loadId: concatUniqueIds(loadsIds, newLoads),
                    });
                }
                setLoad('');
            }
        },
        [filters, setFilters],
    );

    const onPaste = useCallback(
        (event: any) => {
            const loadsIds: string[] = filters.current?.loadId ?? [];
            const data = event.clipboardData.getData('Text');
            const newLoads: string[] = sanitizeFilterIds(data);
            const uniqueIds = concatUniqueIds(loadsIds, newLoads);
            setFilters({ ...filters.current, loadId: uniqueIds });
            event.preventDefault();
        },
        [filters, setFilters],
    );

    return (
        <div className="loads-filters__left-side">
            {!hideLoadField && (
                <div className="loads-filter__load-id">
                    <Input
                        id="load-id-filter"
                        placeholder={t('FilterLoadPlaceholderBuscarCargas')}
                        label={t('CargasID')}
                        value={load}
                        mask="loads"
                        icon="search"
                        onChange={(event: any) => {
                            setLoad(event.target.value);
                        }}
                        onKeyDown={onKeyDown}
                        onPaste={onPaste}
                        dataTestId="loads-filters__load-id"
                    />
                </div>
            )}
            <div className="loads-filter__load-status">
                <SelectAutocomplete
                    id="load-status-filter"
                    mode="multiple"
                    label={t('Status')}
                    value={filters.current?.loadStatus}
                    onChange={(e: any) => {
                        setFilters({
                            ...filters.current,
                            loadStatus: e,
                        });
                    }}
                    placeholder={t('Selecionar')}
                    options={getLoadStatusOptions()}
                    maxTagCount="responsive"
                    data-testid="loads-filters__load-status"
                    notFoundContent={t('NaoHaDadosParaExibir')}
                />
            </div>
            <div className="loads-filter__load-channel">
                <SelectAutocomplete
                    id="load-channel-filter"
                    mode="multiple"
                    label={t('Canal')}
                    value={filters.current?.channels}
                    onChange={(e: any) => {
                        setFilters({ ...filters.current, channels: e });
                    }}
                    placeholder={t('Selecionar')}
                    options={getLoadChannelOptions()}
                    maxTagCount="responsive"
                    data-testid="loads-filters__load-channel"
                    notFoundContent={t('NaoHaDadosParaExibir')}
                />
            </div>
            {getLoadCategoryOptions && (
                <div className="loads-filter__load-category">
                    <SelectAutocomplete
                        id="load-category-filter"
                        label={t('Category')}
                        mode="multiple"
                        value={filters.current?.loadCategories}
                        onChange={(e: any) => {
                            setFilters({
                                ...filters.current,
                                loadCategories: e,
                            });
                        }}
                        placeholder={t('Selectionar')}
                        options={getLoadCategoryOptions()}
                        data-testid="loads-filters__load-category"
                    />
                </div>
            )}
            {setOpenAdvancedFilter && (
                <div
                    className="loads-filter__advanced-filter"
                    data-testid="loads-filters__advanced-filter-container"
                >
                    <ClbTooltip
                        description={t('Filtrar')}
                        position="bottom"
                        showArrow
                        colorMode="dark"
                    >
                        <ClbButton
                            icon="filter"
                            size="lg"
                            styleType="icon"
                            data-testid="loads-filters__advanced-filter-btn"
                            onClick={setOpenAdvancedFilter}
                        />
                    </ClbTooltip>
                </div>
            )}
        </div>
    );
}
